import React, { useRef, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, IconButton, Tooltip, Button, Divider, Chip, CircularProgress,
  TextField, Checkbox, Select, MenuItem, InputAdornment,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import SearchIcon from '@material-ui/icons/Search';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import SortIcon from '@material-ui/icons/Sort';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import PersonIcon from '@material-ui/icons/Person';
import RoomIcon from '@material-ui/icons/Room';
import FlagIcon from '@material-ui/icons/Flag';
import ScheduleIcon from '@material-ui/icons/Schedule';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import WarningIcon from '@material-ui/icons/Warning';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import GetAppIcon from '@material-ui/icons/GetApp';
import PublishIcon from '@material-ui/icons/Publish';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import DirectionsIcon from '@material-ui/icons/Directions';
import {
  fetchSavedRoutesPaginated, bulkDeleteRoutes, bulkUpdateStatus, fetchRouteDetail,
  selectSavedPaginated, selectSavedPagination, selectSavedFilters, selectSavedSort,
  selectSavedPaginatedLoading, setSavedFilters, setSavedSort, setSavedPage,
  deleteRouteFromAPI, selectSaving,
  selectCurrentRoute, saveRouteToAPI, importRoutes, clearRoute,
} from '../../store/slices/routesSlice';
import { setSidebarTab, setShowDetails, selectUI } from '../../store/slices/uiSlice';
import ConfirmDialog from '../common/ConfirmDialog';
import { t } from '../../i18n';
import SectionTitle from '../common/SectionTitle';
import GlassCard from '../common/GlassCard';
import { AuthContext } from '../../App';

/* ─── Constants ─── */

const STATUS_CONFIG = {
  draft:       { label: 'Rascunho',  color: '#94a3b8', bg: 'rgba(148,163,184,0.10)', border: 'rgba(148,163,184,0.25)' },
  planned:     { label: 'Planeada',  color: '#3b82f6', bg: 'rgba(59,130,246,0.10)',  border: 'rgba(59,130,246,0.25)' },
  in_progress: { label: 'Em Trânsito', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)',  border: 'rgba(245,158,11,0.25)' },
  completed:   { label: 'Concluída', color: '#22c55e', bg: 'rgba(34,197,94,0.10)',   border: 'rgba(34,197,94,0.25)' },
  cancelled:   { label: 'Cancelada', color: '#ef4444', bg: 'rgba(239,68,68,0.10)',   border: 'rgba(239,68,68,0.25)' },
};

const STATUS_ICONS = {
  draft:       EditIcon,
  planned:     ScheduleIcon,
  in_progress: WarningIcon,
  completed:   CheckCircleIcon,
  cancelled:   CancelIcon,
};

const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Data' },
  { value: 'name',      label: 'Nome' },
  { value: 'distance',  label: 'Distância' },
  { value: 'duration',  label: 'Duração' },
];

/* ─── Styles ─── */

const useStyles = makeStyles(() => ({
  root: {
    padding: '10px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  searchField: {
    '& .MuiInputBase-root': { fontSize: '0.75rem', height: 32, borderRadius: 8, background: '#f8fafc' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.10)' },
  },
  filterRow: {
    display: 'flex',
    gap: 4,
    flexWrap: 'wrap',
  },
  filterChip: {
    height: 22,
    fontSize: '0.62rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s',
    '&:hover': { opacity: 0.85 },
  },
  sortRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  sortSelect: {
    fontSize: '0.72rem',
    height: 28,
    flex: 1,
    '& .MuiSelect-select': { paddingTop: 4, paddingBottom: 4, fontSize: '0.72rem' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.10)' },
  },
  sortDirBtn: {
    padding: 3,
    fontSize: 14,
    color: '#64748b',
    '&:hover': { color: '#3b82f6' },
  },
  routeCard: {
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    cursor: 'pointer',
    transition: 'box-shadow 0.15s',
    position: 'relative',
    '&:hover': { boxShadow: '0 2px 12px rgba(59,130,246,0.12)' },
  },
  routeHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 4,
  },
  checkboxCell: {
    padding: 0,
    marginRight: 4,
    flexShrink: 0,
  },
  routeName: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#0f172a',
    flex: 1,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  actions: {
    display: 'flex',
    gap: 2,
    flexShrink: 0,
  },
  actionBtn: {
    padding: 3,
    color: '#94a3b8',
    '&:hover': { color: '#3b82f6' },
  },
  deleteBtn: {
    padding: 3,
    color: '#94a3b8',
    '&:hover': { color: '#ef4444' },
  },
  routePoint: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0,
  },
  pointText: {
    fontSize: '0.68rem',
    color: '#475569',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  metaRow: {
    display: 'flex',
    gap: 5,
    flexWrap: 'wrap',
    alignItems: 'center',
    marginTop: 2,
  },
  metaChip: {
    height: 17,
    fontSize: '0.58rem',
    fontWeight: 600,
  },
  statusBadge: {
    height: 18,
    fontSize: '0.58rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
  },
  loadBtn: {
    marginTop: 4,
    background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
    color: '#ffffff',
    fontSize: '0.70rem',
    padding: '4px 10px',
    fontWeight: 600,
    '&:hover': { background: 'linear-gradient(135deg, #60a5fa 0%, #818cf8 100%)' },
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '28px 16px',
    gap: 8,
    textAlign: 'center',
  },
  toolbarRow: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  },
  toolBtn: {
    flex: 1,
    fontSize: '0.70rem',
    padding: '5px 6px',
    textTransform: 'none',
    fontWeight: 600,
    minWidth: 0,
  },
  paginationRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: '4px 0',
  },
  pageInfo: {
    fontSize: '0.68rem',
    color: '#64748b',
    fontWeight: 500,
    minWidth: 80,
    textAlign: 'center',
  },
  pageBtn: {
    padding: 4,
    color: '#3b82f6',
    '&.Mui-disabled': { color: '#cbd5e1' },
  },
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    background: 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(99,102,241,0.08))',
    borderRadius: 8,
    border: '1px solid rgba(59,130,246,0.15)',
  },
  bulkText: {
    fontSize: '0.68rem',
    color: '#3b82f6',
    fontWeight: 600,
    flex: 1,
  },
  bulkBtn: {
    fontSize: '0.65rem',
    padding: '3px 8px',
    textTransform: 'none',
    fontWeight: 600,
    minWidth: 0,
  },
  skeletonCard: {
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  skeletonLine: {
    height: 10,
    borderRadius: 4,
    background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)',
    backgroundSize: '200% 100%',
    animation: '$shimmer 1.5s infinite',
  },
  '@keyframes shimmer': {
    '0%': { backgroundPosition: '200% 0' },
    '100%': { backgroundPosition: '-200% 0' },
  },
  costText: {
    fontSize: '0.62rem',
    color: '#6366f1',
    fontWeight: 600,
  },
  driversText: {
    fontSize: '0.62rem',
    color: '#8b5cf6',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 3,
  },
  dateText: {
    fontSize: '0.58rem',
    color: '#94a3b8',
  },
}));

/* ─── Helpers ─── */

function formatDistance(m) {
  if (!m) return null;
  return `${(m / 1000).toFixed(1)} km`;
}

function formatDuration(sec) {
  if (!sec) return null;
  const h = Math.floor(sec / 3600);
  const min = Math.round((sec % 3600) / 60);
  if (h > 0) return `${h}h ${min}m`;
  return `${min}m`;
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('pt-PT', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return null; }
}

function downloadJson(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function estimateCost(route) {
  const dist = route.computedRoute?.distance;
  const consumption = route.fuelConsumption || 32; // L/100km default truck
  const price = route.fuelPrice || 1.55;
  if (!dist) return null;
  const litres = (dist / 1000) * (consumption / 100);
  return (litres * price).toFixed(2);
}

/* ─── Skeleton Loader ─── */

function SkeletonCards({ classes, count = 4 }) {
  return (
    <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {Array.from({ length: count }).map((_, i) => (
        <GlassCard key={i} flat className={classes.skeletonCard}>
          <Box className={classes.skeletonLine} style={{ width: '65%', height: 12 }} />
          <Box className={classes.skeletonLine} style={{ width: '85%' }} />
          <Box className={classes.skeletonLine} style={{ width: '80%' }} />
          <Box style={{ display: 'flex', gap: 6 }}>
            <Box className={classes.skeletonLine} style={{ width: 50, height: 16, borderRadius: 8 }} />
            <Box className={classes.skeletonLine} style={{ width: 40, height: 16, borderRadius: 8 }} />
            <Box className={classes.skeletonLine} style={{ width: 55, height: 16, borderRadius: 8 }} />
          </Box>
        </GlassCard>
      ))}
    </Box>
  );
}

/* ─── Status Badge ─── */

function StatusBadge({ status, classes }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <Chip
      label={cfg.label}
      size="small"
      className={classes.statusBadge}
      style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
    />
  );
}

/* ─── Main Component ─── */

function SavedRoutesList() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const routes = useSelector(selectSavedPaginated);
  const pagination = useSelector(selectSavedPagination);
  const filters = useSelector(selectSavedFilters);
  const sort = useSelector(selectSavedSort);
  const loading = useSelector(selectSavedPaginatedLoading);
  const saving = useSelector(selectSaving);
  const currentRoute = useSelector(selectCurrentRoute);
  const { lang } = useSelector(selectUI);
  const fileInputRef = useRef(null);

  const [selected, setSelected] = useState([]);
  const [bulkDeletePending, setBulkDeletePending] = useState(null);
  const [singleDeletePending, setSingleDeletePending] = useState(null);
  const [bulkDeleteError, setBulkDeleteError] = useState('');
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [bulkStatusValue, setBulkStatusValue] = useState('');
  const searchTimerRef = useRef(null);

  const { page } = pagination;

  // Fetch paginated routes on mount and whenever deps change
  useEffect(() => {
    if (!token) return;
    dispatch(fetchSavedRoutesPaginated({ token, page, limit: pagination.limit, filters, sort }));
  }, [dispatch, token, page, filters, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      dispatch(setSavedFilters({ search: val }));
    }, 400);
  }, [dispatch]);

  // Status filter
  const handleStatusFilter = useCallback((status) => {
    dispatch(setSavedFilters({ status: filters.status === status ? '' : status }));
    setSelected([]);
  }, [dispatch, filters.status]);

  const handleClearStatusFilter = useCallback(() => {
    dispatch(setSavedFilters({ status: '' }));
    setSelected([]);
  }, [dispatch]);

  // Sort
  const handleSortChange = useCallback((e) => {
    dispatch(setSavedSort({ field: e.target.value, dir: sort.dir }));
  }, [dispatch, sort.dir]);

  const toggleSortDir = useCallback(() => {
    dispatch(setSavedSort({ field: sort.field, dir: sort.dir === 'asc' ? 'desc' : 'asc' }));
  }, [dispatch, sort.field, sort.dir]);

  // Pagination
  const handlePrevPage = useCallback(() => {
    if (page > 1) dispatch(setSavedPage(page - 1));
  }, [dispatch, page]);

  const handleNextPage = useCallback(() => {
    if (page < pagination.totalPages) dispatch(setSavedPage(page + 1));
  }, [dispatch, page, pagination.totalPages]);

  // Selection
  const handleToggleSelect = useCallback((dbId) => {
    setSelected(prev => prev.includes(dbId) ? prev.filter(id => id !== dbId) : [...prev, dbId]);
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selected.length === routes.length) {
      setSelected([]);
    } else {
      setSelected(routes.map(r => r.dbId).filter(Boolean));
    }
  }, [selected.length, routes]);

  // Bulk actions
  const handleBulkDelete = useCallback(() => {
    if (!token || selected.length === 0) return;
    // Filter out dispatched/in-progress routes
    const deletable = selected.filter(id => {
      const r = routes.find(rt => rt.dbId === id || rt.id === id);
      const st = r?.dbStatus || r?.status;
      return st !== 'planned' && st !== 'in_progress';
    });
    if (deletable.length === 0) {
      setBulkDeleteError(lang === 'pt' ? 'Nenhuma rota pode ser eliminada (todas estão despachadas ou em trânsito).' : 'No routes can be deleted (all dispatched or in-transit).');
      return;
    }
    setBulkDeletePending(deletable);
  }, [dispatch, token, selected, page, pagination.limit, filters, sort]);

  const confirmBulkDelete = useCallback(() => {
    if (!bulkDeletePending) return;
    dispatch(bulkDeleteRoutes({ token, routeIds: bulkDeletePending })).then(() => {
      setSelected([]);
      dispatch(fetchSavedRoutesPaginated({ token, page, limit: pagination.limit, filters, sort }));
    });
    setBulkDeletePending(null);
  }, [dispatch, token, bulkDeletePending, page, pagination.limit, filters, sort]);

  const handleBulkStatus = useCallback(() => {
    if (!token || selected.length === 0 || !bulkStatusValue) return;
    dispatch(bulkUpdateStatus({ token, routeIds: selected, status: bulkStatusValue })).then(() => {
      setSelected([]);
      setBulkStatusValue('');
      dispatch(fetchSavedRoutesPaginated({ token, page, limit: pagination.limit, filters, sort }));
    });
  }, [dispatch, token, selected, bulkStatusValue, page, pagination.limit, filters, sort]);

  // Route actions
  const handleLoad = useCallback((route) => {
    if (!token || !route.dbId) return;
    dispatch(clearRoute());
    setTimeout(() => {
      dispatch(fetchRouteDetail({ token, routeId: route.dbId }));
      dispatch(setShowDetails(true));
      dispatch(setSidebarTab(0));
    }, 100);
  }, [dispatch, token]);

  const handleDelete = useCallback((route) => {
    if (!token || !route.dbId) return;
    const st = route.dbStatus || route.status;
    if (st === 'planned' || st === 'in_progress') {
      setBulkDeleteError(lang === 'pt' ? 'Não é possível eliminar uma rota que já foi despachada ou está em trânsito.' : 'Cannot delete a dispatched or in-transit route.');
      return;
    }
    setSingleDeletePending(route.dbId);
  }, [token, lang]);

  const confirmSingleDelete = useCallback(() => {
    if (!singleDeletePending) return;
    dispatch(deleteRouteFromAPI({ token, routeId: singleDeletePending })).then((result) => {
      if (result.error && result.payload?.code === 'linked') {
        // Route is linked to OTs — ask the operator to confirm forcing
        const otList = (result.payload.linkedOTs || [])
          .map(o => `· OT ${o.orderNumber || o.id}${o.vehiclePlate ? ' · ' + o.vehiclePlate : ''} (${o.status})`)
          .join('\n');
        const msg = (lang === 'pt'
          ? `Esta rota está associada a ${result.payload.linkedOTs?.length || 0} ordem(s) de transporte:\n\n${otList}\n\nAo eliminar, as OTs ficam desassociadas e voltam a "pendente". Continuar?`
          : `This route is linked to ${result.payload.linkedOTs?.length || 0} order(s):\n\n${otList}\n\nDeleting will unlink them and revert to "pending". Continue?`);
        if (window.confirm(msg)) {
          dispatch(deleteRouteFromAPI({ token, routeId: singleDeletePending, force: true })).then(() => {
            dispatch(fetchSavedRoutesPaginated({ token, page, limit: pagination.limit, filters, sort }));
          });
        }
      } else {
        dispatch(fetchSavedRoutesPaginated({ token, page, limit: pagination.limit, filters, sort }));
      }
    });
    setSingleDeletePending(null);
  }, [dispatch, token, singleDeletePending, page, pagination.limit, filters, sort, lang]);

  const handleSaveCurrent = useCallback(() => {
    if (!currentRoute.origin || !currentRoute.destination) return;
    if (!token) return;
    dispatch(saveRouteToAPI({ token, route: currentRoute })).then(() => {
      dispatch(fetchSavedRoutesPaginated({ token, page, limit: pagination.limit, filters, sort }));
    });
  }, [dispatch, token, currentRoute, page, pagination.limit, filters, sort]);

  const handleExportAll = useCallback(() => {
    downloadJson(routes, `ecoogps_rotas_${new Date().toISOString().slice(0, 10)}.json`);
  }, [routes]);

  const handleImportFile = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = evt => {
      try {
        const data = JSON.parse(evt.target.result);
        dispatch(importRoutes(data));
      } catch {
        alert(t(lang, 'invalidJSON'));
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }, [dispatch, lang]);

  const allStatuses = useMemo(() => Object.keys(STATUS_CONFIG), []);

  return (<>
    <Box className={classes.root}>
      <SectionTitle icon={BookmarkIcon}>{t(lang, 'savedRoutesTitle')}</SectionTitle>

      {/* Save current route button */}
      {currentRoute.origin && currentRoute.destination && (
        <GlassCard flat padding="sm">
          <Typography style={{ fontSize: '0.70rem', color: '#64748b', marginBottom: 5 }}>
            {t(lang, 'currentRouteLabel')}{' '}
            <strong style={{ color: '#0f172a' }}>{currentRoute.name || t(lang, 'noNameRoute')}</strong>
          </Typography>
          <Button
            fullWidth variant="contained" size="small"
            className={classes.loadBtn}
            startIcon={saving ? <CircularProgress size={12} style={{ color: '#fff' }} /> : <BookmarkIcon style={{ fontSize: 13 }} />}
            onClick={handleSaveCurrent}
            disabled={saving}
          >
            {saving ? 'A guardar...' : t(lang, 'saveCurrentRouteBtn')}
          </Button>
        </GlassCard>
      )}

      {/* Import / Export toolbar */}
      <Box className={classes.toolbarRow}>
        <Button
          variant="outlined" size="small" className={classes.toolBtn}
          startIcon={<PublishIcon style={{ fontSize: 13 }} />}
          onClick={() => fileInputRef.current?.click()}
          style={{ borderColor: 'rgba(0,0,0,0.12)', color: '#475569' }}
        >
          {t(lang, 'importJSON')}
        </Button>
        <Button
          variant="outlined" size="small" className={classes.toolBtn}
          startIcon={<GetAppIcon style={{ fontSize: 13 }} />}
          onClick={handleExportAll}
          disabled={routes.length === 0}
          style={{ borderColor: 'rgba(0,0,0,0.12)', color: '#475569' }}
        >
          {t(lang, 'exportAll')}
        </Button>
        <input ref={fileInputRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleImportFile} />
      </Box>

      <Divider style={{ margin: '2px 0' }} />

      {/* Search bar */}
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Pesquisar rotas..."
        value={searchInput}
        onChange={handleSearchChange}
        className={classes.searchField}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon style={{ fontSize: 16, color: '#94a3b8' }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Status filter chips */}
      <Box className={classes.filterRow}>
        <Chip
          label="Todos"
          size="small"
          className={classes.filterChip}
          onClick={handleClearStatusFilter}
          style={!filters.status ? {
            background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
            color: '#fff',
          } : {
            background: 'rgba(0,0,0,0.04)',
            color: '#64748b',
            border: '1px solid rgba(0,0,0,0.08)',
          }}
        />
        {allStatuses.map(s => {
          const cfg = STATUS_CONFIG[s];
          const isActive = filters.status === s;
          return (
            <Chip
              key={s}
              label={cfg.label}
              size="small"
              className={classes.filterChip}
              onClick={() => handleStatusFilter(s)}
              style={isActive ? {
                background: cfg.color,
                color: '#fff',
              } : {
                background: cfg.bg,
                color: cfg.color,
                border: `1px solid ${cfg.border}`,
              }}
            />
          );
        })}
      </Box>

      {/* Sort controls */}
      <Box className={classes.sortRow}>
        <SortIcon style={{ fontSize: 14, color: '#94a3b8' }} />
        <Select
          variant="outlined"
          value={sort.field}
          onChange={handleSortChange}
          className={classes.sortSelect}
        >
          {SORT_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value} style={{ fontSize: '0.72rem' }}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
        <Tooltip title={sort.dir === 'asc' ? 'Ascendente' : 'Descendente'}>
          <IconButton className={classes.sortDirBtn} onClick={toggleSortDir} size="small">
            <Typography style={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b' }}>
              {sort.dir === 'asc' ? '↑' : '↓'}
            </Typography>
          </IconButton>
        </Tooltip>
      </Box>

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <Box className={classes.bulkBar}>
          <Checkbox
            size="small"
            checked={selected.length === routes.length}
            indeterminate={selected.length > 0 && selected.length < routes.length}
            onChange={handleSelectAll}
            style={{ padding: 2, color: '#3b82f6' }}
          />
          <Typography className={classes.bulkText}>
            {selected.length} seleccionada{selected.length > 1 ? 's' : ''}
          </Typography>
          <Button
            size="small" variant="outlined"
            className={classes.bulkBtn}
            startIcon={<DeleteIcon style={{ fontSize: 12 }} />}
            onClick={handleBulkDelete}
            style={{ borderColor: '#ef4444', color: '#ef4444' }}
          >
            Eliminar
          </Button>
          <Select
            variant="outlined"
            value={bulkStatusValue}
            onChange={e => setBulkStatusValue(e.target.value)}
            displayEmpty
            style={{ fontSize: '0.65rem', height: 24, minWidth: 80 }}
          >
            <MenuItem value="" disabled style={{ fontSize: '0.65rem' }}>Status...</MenuItem>
            {allStatuses.map(s => (
              <MenuItem key={s} value={s} style={{ fontSize: '0.65rem' }}>{STATUS_CONFIG[s].label}</MenuItem>
            ))}
          </Select>
          <Button
            size="small" variant="outlined"
            className={classes.bulkBtn}
            startIcon={<EditIcon style={{ fontSize: 12 }} />}
            onClick={handleBulkStatus}
            disabled={!bulkStatusValue}
            style={{ borderColor: '#3b82f6', color: '#3b82f6' }}
          >
            Alterar
          </Button>
        </Box>
      )}

      <Divider style={{ margin: '2px 0' }} />

      {/* Route list or states */}
      {loading ? (
        <SkeletonCards classes={classes} />
      ) : routes.length === 0 ? (
        <Box className={classes.emptyState}>
          <FolderOpenIcon style={{ fontSize: 36, color: '#cbd5e1' }} />
          <Typography style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 500 }}>
            {filters.search || filters.status ? 'Nenhuma rota encontrada' : t(lang, 'noSavedRoutes')}
          </Typography>
          <Typography style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
            {filters.search || filters.status ? 'Tente alterar os filtros de pesquisa' : t(lang, 'savedRoutesHint')}
          </Typography>
        </Box>
      ) : (
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {routes.map(route => {
            const distStr = formatDistance(route.computedRoute?.distance);
            const durStr = formatDuration(route.computedRoute?.duration);
            const dateStr = formatDate(route.savedAt);
            const isActive = currentRoute.computedRoute && (route.id === currentRoute.id || (route.dbId && route.dbId === currentRoute.dbId));
            const isSelected = selected.includes(route.dbId);
            const cost = estimateCost(route);
            const driverCount = route.routeDrivers?.length || 0;
            const status = route.dbStatus || 'draft';

            return (
              <GlassCard
                key={route.id}
                className={classes.routeCard}
                flat
                style={{ borderLeft: isActive ? '3px solid #3b82f6' : '3px solid transparent' }}
              >
                {/* Header: checkbox + name + actions */}
                <Box className={classes.routeHeader}>
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onChange={() => handleToggleSelect(route.dbId)}
                    className={classes.checkboxCell}
                    style={{ color: isSelected ? '#3b82f6' : '#cbd5e1' }}
                  />
                  <Typography className={classes.routeName}>
                    {route.name || t(lang, 'routeNoName')}
                  </Typography>
                  <Box className={classes.actions}>
                    <StatusBadge status={status} classes={classes} />
                    <Tooltip title={t(lang, 'delete')}>
                      <IconButton size="small" className={classes.deleteBtn} onClick={(e) => { e.stopPropagation(); handleDelete(route); }}>
                        <DeleteIcon style={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Origin → Destination */}
                <Box className={classes.routePoint}>
                  <RoomIcon style={{ fontSize: 11, color: '#22c55e' }} />
                  <Typography className={classes.pointText}>{route.origin?.name || '—'}</Typography>
                </Box>
                <Box className={classes.routePoint}>
                  <FlagIcon style={{ fontSize: 11, color: '#ef4444' }} />
                  <Typography className={classes.pointText}>{route.destination?.name || '—'}</Typography>
                </Box>

                {/* Meta row: distance, duration, cost, drivers, date */}
                <Box className={classes.metaRow}>
                  {distStr && (
                    <Chip label={distStr} size="small" className={classes.metaChip}
                      style={{ background: 'rgba(59,130,246,0.08)', color: '#3b82f6', border: '1px solid rgba(59,130,246,0.2)' }} />
                  )}
                  {durStr && (
                    <Chip label={durStr} size="small" className={classes.metaChip}
                      style={{ background: 'rgba(245,158,11,0.08)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.2)' }} />
                  )}
                  {cost && (
                    <Typography className={classes.costText}>€{cost}</Typography>
                  )}
                  {driverCount > 0 && (
                    <Typography className={classes.driversText}>
                      <PersonIcon style={{ fontSize: 10 }} />
                      {driverCount}
                    </Typography>
                  )}
                  {dateStr && (
                    <Typography className={classes.dateText}>{dateStr}</Typography>
                  )}
                </Box>

                {/* Load button */}
                {!isActive ? (
                  <Button
                    size="small" variant="contained"
                    className={classes.loadBtn}
                    startIcon={<DirectionsIcon style={{ fontSize: 12 }} />}
                    onClick={() => handleLoad(route)}
                  >
                    {t(lang, 'loadRouteBtn')}
                  </Button>
                ) : (
                  <Typography style={{ fontSize: '0.62rem', color: '#3b82f6', fontWeight: 600, marginTop: 1 }}>
                    {t(lang, 'activeRouteLabel')}
                  </Typography>
                )}
              </GlassCard>
            );
          })}
        </Box>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Box className={classes.paginationRow}>
          <IconButton
            className={classes.pageBtn}
            onClick={handlePrevPage}
            disabled={page <= 1}
            size="small"
          >
            <ChevronLeftIcon style={{ fontSize: 18 }} />
          </IconButton>
          <Typography className={classes.pageInfo}>
            Página {page} de {pagination.totalPages}
          </Typography>
          <IconButton
            className={classes.pageBtn}
            onClick={handleNextPage}
            disabled={page >= pagination.totalPages}
            size="small"
          >
            <ChevronRightIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}

      {/* Total count */}
      {pagination.total > 0 && (
        <Typography style={{ fontSize: '0.60rem', color: '#94a3b8', textAlign: 'center' }}>
          {pagination.total} rota{pagination.total !== 1 ? 's' : ''} no total
        </Typography>
      )}
    </Box>
    <ConfirmDialog
      open={!!bulkDeletePending}
      onClose={() => setBulkDeletePending(null)}
      onConfirm={confirmBulkDelete}
      title={lang === 'pt' ? 'Eliminar Rotas' : 'Delete Routes'}
      message={lang === 'pt' ? `Tem a certeza que deseja eliminar ${bulkDeletePending?.length || 0} rota(s)?` : `Are you sure you want to delete ${bulkDeletePending?.length || 0} route(s)?`}
      severity="error"
      confirmLabel={lang === 'pt' ? 'Eliminar' : 'Delete'}
    />
    <ConfirmDialog
      open={!!singleDeletePending}
      onClose={() => setSingleDeletePending(null)}
      onConfirm={confirmSingleDelete}
      title={lang === 'pt' ? 'Eliminar Rota' : 'Delete Route'}
      message={lang === 'pt' ? 'Tem a certeza que deseja eliminar esta rota?' : 'Are you sure you want to delete this route?'}
      severity="error"
      confirmLabel={lang === 'pt' ? 'Eliminar' : 'Delete'}
    />
    <ConfirmDialog
      open={!!bulkDeleteError}
      onClose={() => setBulkDeleteError('')}
      title={lang === 'pt' ? 'Aviso' : 'Warning'}
      message={bulkDeleteError}
      severity="warning"
    />
    </>
  );
}

export default SavedRoutesList;
